/**
 * Workflow API — Consolidated workflow functions from workflowDataService.js
 *
 * Source: public/SPIGlassApp/services/workflowDataService.js
 * Contains all workflow query + mutation functions.
 * Previously split between this file and auditDashboardApi.ts — now unified here.
 */

import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';
import { validDynamic } from '@/lib/permissionsApi';

const API_BASE = `${buildApiBase()}/API/Workflow`;
const WO_API_BASE = `${buildApiBase()}/API`;

// ============ TYPES ============

export interface WorkflowState {
  id: number;
  name?: string;
  code: string;
}

export interface ValidWorkflowTransition {
  id: number;
  name: string;
  toState: WorkflowState;
  fromStateId?: number;
  transPermission?: string;
  count?: number;
}

export interface WorkflowStateReason {
  id: number;
  reason: string;
}

export interface WorkflowTransitionHistory {
  id: number;
  currentState?: WorkflowState;
  currentStateId?: number;
  workflowStateReason?: { reason?: string };
  remarks?: string;
  createdBy?: string;
  toDate?: string;
}

export interface SetFinancialWorkFlowResponse {
  success: boolean;
  message?: string;
}

// ============ QUERY FUNCTIONS ============

/**
 * workflowDataService.GetValidWorkflowStates
 *
 * Generic function that appends the workflow type suffix to the URL:
 *   bid      → GetValidWorkflowStatesForBid
 *   approval → GetValidWorkflowStatesForApproval
 *   task     → GetValidWorkflowStatesForTasks
 *   workOrder → GetValidWorkflowStatesForWorkOrder
 *   finance  → GetValidWorkflowStatesForFinance
 */
export async function getValidWorkflowStates(
  currentStateId: number,
  workflowType: string,
  flowName?: string
): Promise<ValidWorkflowTransition[]> {
  let url = `${API_BASE}/GetValidWorkflowStates`;

  switch (workflowType) {
    case 'bid':
      url += 'ForBid';
      break;
    case 'approval':
      url += 'ForApproval';
      break;
    case 'task':
      url += 'ForTasks';
      break;
    case 'workOrder':
      url += 'ForWorkOrder';
      break;
    case 'finance':
      url += 'ForFinance';
      break;
  }

  const params: Record<string, unknown> = { currentStateId };
  if (flowName) {
    params.flowName = flowName;
  }

  const response = await axiosClient.get<ValidWorkflowTransition[]>(url, { params });

  // Post-processing: filter by user permissions — matches AngularJS workflowDataService.js lines 66-81
  // Each workflow state has a transPermission string that must be checked via validDynamic()
  const data = response.data ?? [];
  if (data.length === 0) return data;

  const validStates: ValidWorkflowTransition[] = [];
  for (const wfState of data) {
    if (validDynamic(wfState.transPermission ?? '')) {
      validStates.push(wfState);
    }
  }
  return validStates;
}

/** GET /API/Workflow/GetValidWorkflowStatesForWorkOrder */
export async function getValidWorkOrderWorkflowStates(currentStateId: number) {
  const response = await axiosClient.get<ValidWorkflowTransition[]>(
    `${API_BASE}/GetValidWorkflowStatesForWorkOrder`,
    { params: { currentStateId } }
  );
  return response.data;
}

/** GET /API/Workflow/GetValidWorkflowStates?workflowType=finance */
export async function getValidFinanceWorkflowStates(currentStateId: number) {
  const response = await axiosClient.get<ValidWorkflowTransition[]>(
    `${API_BASE}/GetValidWorkflowStates`,
    { params: { currentStateId, workflowType: 'finance' } }
  );
  return response.data;
}

/** GET /API/Workflow/GetValidReasons
 *  AngularJS workflowDataService.js declares GetValidReasons twice (lines 86
 *  and 181); the second declaration wins and sends `WorkflowStateId` (capital).
 *  Match that wire format exactly.
 */
export async function getValidReasons(workflowStateId: number): Promise<WorkflowStateReason[]> {
  const response = await axiosClient.get<WorkflowStateReason[]>(`${API_BASE}/GetValidReasons`, {
    params: { WorkflowStateId: workflowStateId },
  });
  return response.data;
}

/** GET /API/Workflow/GetStateTransitionHistory */
export async function getStateTransitionHistory(workflowStateTransitionId: number) {
  const response = await axiosClient.get<WorkflowTransitionHistory[]>(
    `${API_BASE}/GetStateTransitionHistory`,
    { params: { WorkflowStateTransitionId: workflowStateTransitionId } }
  );
  return response.data;
}

/**
 * GET /API/Workflow/GetStateTransitionHistory?flowName=...&orderNumber=...
 * AngularJS: workflowDataService.GetStateTransitionHistorySubFlow(orderNumber, flowName)
 */
export async function getStateTransitionHistorySubFlow(
  orderNumber: string | number,
  flowName: string
) {
  const response = await axiosClient.get<WorkflowTransitionHistory[]>(
    `${API_BASE}/GetStateTransitionHistory`,
    { params: { flowName, orderNumber } }
  );
  return response.data;
}

/** GET /API/Workflow/GetCurrentWorkflowState — not yet used but ported for completeness */
export async function getCurrentFinancialWorkflowState(orderNumber: string) {
  const response = await axiosClient.get<ValidWorkflowTransition[]>(
    `${API_BASE}/GetCurrentWorkflowState`,
    { params: { orderNumber, flowName: 'WorkOrder Financials' } }
  );
  return response.data;
}

/**
 * GET /API/Workflow/GetCurrentWorkflowState — by transition ID
 * (non-financial variant — the financial variant above uses orderNumber + flowName)
 * AngularJS: workflowDataService.GetCurrentWorkflowState(WorkflowStateTransitionId)
 */
export async function getCurrentWorkflowState(workflowStateTransitionId: number) {
  const response = await axiosClient.get(`${API_BASE}/GetCurrentWorkflowState`, {
    params: { WorkflowStateTransitionId: workflowStateTransitionId },
  });
  return response.data;
}

/**
 * GET /API/Workflow/GetWorkflowAllowedStateTransitionByWorkFlowId
 * AngularJS: workflowDataService.GetWorkflowAllowedStateTransitionByWorkFlowId(WorkFlowId)
 */
export async function getWorkflowAllowedStateTransitionByWorkFlowId(workFlowId: number) {
  const response = await axiosClient.get(
    `${API_BASE}/GetWorkflowAllowedStateTransitionByWorkFlowId`,
    { params: { WorkFlowId: workFlowId } }
  );
  return response.data;
}

/**
 * Compose a synthetic "Pre-SafeView Audit" workflow state on the work order object.
 * This is a client-side factory function (no API call).
 * AngularJS: workflowDataService.GetComposedWorkflowState(workorder) — lines 91-126
 */
export function getComposedWorkflowState<T extends { workflowStateTransition?: unknown }>(
  workorder: T
): T {
  workorder.workflowStateTransition = {
    createdBy: 'SafeView Frontend',
    createdDate: new Date().toISOString(),
    currentState: {
      active: true,
      allowSpiAudit: true,
      allowSpiOrderClose: true,
      code: null,
      createdBy: 'SafeView Frontend',
      createdDate: new Date().toISOString(),
      editPermission: null,
      externalName: 'Legacy',
      id: null,
      lastModifiedBy: null,
      lastModifiedDate: null,
      name: 'Pre-SafeView Audit',
    },
    displayStatus: 'Pre-SafeView Audit',
    externalDisplayStatus: 'Legacy',
    id: null,
    lastModifiedBy: 'SafeView Frontend',
    lastModifiedDate: null,
    onBehalfOf: null,
    remarks: 'Status created as part of transition to Workflow States in SafeView',
    transitionHistory: null,
    workflowStateAllowedTransitionId: null,
    workflowStateReason: null,
    workflowStateReasonId: null,
  };
  return workorder;
}

// ============ MUTATION FUNCTIONS ============

/** PUT /API/Workflow/SetWorkOrderState */
export async function setWorkOrderState(params: {
  OrderNumber: string;
  WorkflowStateCode: string;
  UserName: string | null;
  ReasonId: number | null;
  Remarks: string;
  OnBehalfOf: string;
  // AngularJS workflowDataService.SetWorkOrderState always sends `Workflow: flowName`
  // — 'WorkOrder' for the top form, 'WorkOrderPostAuditCalculations' for the sub-flow form.
  Workflow?: string;
}) {
  const response = await axiosClient.put(`${API_BASE}/SetWorkOrderState`, params);
  return response.data;
}

/** PUT /API/Workflow/SetFinancialWorkFlow */
export async function setFinancialWorkFlow(params: {
  OrderNumber: string;
  StateCode: string;
  WorkOrderFinancialTransitionId: number;
  WorkOrderFinancialsWorkflowId: number | null;
  UserName: string | null;
  Remarks: string;
  OnBehalfOf: string;
  WorkflowStateReasonId: number | null;
  StateId: number;
}) {
  const response = await axiosClient.put<SetFinancialWorkFlowResponse>(
    `${API_BASE}/SetFinancialWorkFlow`,
    params
  );
  return response.data;
}

/** PUT /API/WorkOrderInfo/Cancel */
export async function cancelOrder(orderNumber: string) {
  const response = await axiosClient.put(`${WO_API_BASE}/WorkOrderInfo/Cancel`, { orderNumber });
  return response.data;
}

/** PUT /API/WorkOrderInfo/UnCancel */
export async function uncancelOrder(orderNumber: string) {
  const response = await axiosClient.put(`${WO_API_BASE}/WorkOrderInfo/UnCancel`, { orderNumber });
  return response.data;
}

/** PUT /API/WorkOrderInfo/ClearCancellationDate */
export async function clearCancellationDate(orderNumber: string) {
  const response = await axiosClient.put<{ success: boolean }>(
    `${WO_API_BASE}/WorkOrderInfo/ClearCancellationDate?orderNumber=${orderNumber}`
  );
  return response.data;
}

/**
 * Complete an order or submission.
 *
 * Port of generalDataService.CompleteOrder(id, itemType) from
 * public/SPIGlassApp/services/generalDataService.js (lines 363-389).
 *
 * When `itemType === 'SUBMISSION'` calls /API/Submission/CompleteSubmission with
 * `ParentSubmissionId`. Otherwise calls /API/WorkOrderInfo/CompleteOrder with
 * `OrderNumber`.
 */
export async function completeOrder(
  id: string | number,
  itemType?: string | null
): Promise<unknown> {
  if (itemType === 'SUBMISSION') {
    const response = await axiosClient.get(`${WO_API_BASE}/Submission/CompleteSubmission/`, {
      params: { ParentSubmissionId: id },
    });
    return response.data;
  }
  const response = await axiosClient.get(`${WO_API_BASE}/WorkOrderInfo/CompleteOrder/`, {
    params: { OrderNumber: id },
  });
  return response.data;
}
